import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Avatar,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  Stack
} from '@mui/material';
import {
  Person,
  Email,
  Star,
  Favorite,
  CalendarMonth,
  LocationOn,
  Upgrade,
  CheckCircle,
  AccountCircle
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useRole, ROLES } from '../../contexts/RoleContext';
import { useFavorites } from '../../context/FavoritesContext';
import { useNavigate } from 'react-router-dom';
import SubscriptionModal from '../modals/SubscriptionModal';

const Profile = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { userRole, subscriptionStatus, isPro } = useRole();
  const { favorites } = useFavorites();
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);

  const isProUser = isPro();
  const favoritesCount = Array.isArray(favorites) ? favorites.length : 0;

  // Format the account creation date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Get display name from email or metadata
  const getDisplayName = () => {
    if (currentUser?.user_metadata?.full_name) {
      return currentUser.user_metadata.full_name;
    }
    if (currentUser?.user_metadata?.name) {
      return currentUser.user_metadata.name;
    }
    if (currentUser?.email) {
      return currentUser.email.split('@')[0];
    }
    return 'User';
  };

  // Get tier label
  const getTierLabel = () => {
    if (userRole === ROLES.ADMIN) return 'Administrator';
    if (userRole === ROLES.PRO_CUSTOMER) return 'Pro Member';
    return 'Basic Member';
  };

  // Get tier color
  const getTierColor = () => {
    if (userRole === ROLES.ADMIN) return '#e91e63';
    if (userRole === ROLES.PRO_CUSTOMER) return '#ffd700';
    return '#9e9e9e';
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      {/* Profile Header */}
      <Paper 
        elevation={0}
        sx={{ 
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          p: 4,
          mb: 3,
          borderRadius: 3
        }}
      >
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          alignItems="center" 
          spacing={3}
          sx={{ textAlign: { xs: 'center', sm: 'left' } }}
        >
          <Avatar 
            sx={{ 
              width: 100, 
              height: 100, 
              bgcolor: 'rgba(255,255,255,0.2)',
              fontSize: '2.5rem',
              border: '3px solid rgba(255,255,255,0.3)'
            }}
          >
            {currentUser?.email?.[0]?.toUpperCase() || 'U'}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
              {getDisplayName()}
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9, mb: 2 }}>
              {currentUser?.email}
            </Typography>
            <Chip 
              icon={isProUser ? <Star sx={{ color: 'inherit !important' }} /> : <AccountCircle />}
              label={getTierLabel()}
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)', 
                color: 'white',
                fontWeight: 'bold',
                borderColor: getTierColor(),
                border: `2px solid ${getTierColor()}`
              }} 
            />
          </Box>
          {!isProUser && userRole !== ROLES.ADMIN && (
            <Button
              variant="contained"
              size="large"
              startIcon={<Upgrade />}
              onClick={() => setSubscriptionModalOpen(true)}
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.3)'
                }
              }}
            >
              Upgrade to Pro
            </Button>
          )}
        </Stack>
      </Paper>

      <Grid container spacing={3}>
        {/* Personal Information */}
        <Grid item xs={12} md={6}>
          <Card elevation={1} sx={{ height: '100%', borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                Personal Information
              </Typography>
              
              <List disablePadding>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Person color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={<Typography variant="body2" color="text.secondary">Name</Typography>}
                    secondary={<Typography variant="body1" fontWeight={500}>{getDisplayName()}</Typography>}
                  />
                </ListItem>
                
                <Divider sx={{ my: 1 }} />
                
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Email color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={<Typography variant="body2" color="text.secondary">Email Address</Typography>}
                    secondary={<Typography variant="body1" fontWeight={500}>{currentUser?.email || 'N/A'}</Typography>}
                  />
                </ListItem>
                
                <Divider sx={{ my: 1 }} />
                
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <CalendarMonth color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={<Typography variant="body2" color="text.secondary">Member Since</Typography>}
                    secondary={<Typography variant="body1" fontWeight={500}>{formatDate(currentUser?.created_at)}</Typography>}
                  />
                </ListItem>
                
                <Divider sx={{ my: 1 }} />
                
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <LocationOn color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={<Typography variant="body2" color="text.secondary">Region</Typography>}
                    secondary={<Typography variant="body1" fontWeight={500}>New Jersey</Typography>}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Account Details */}
        <Grid item xs={12} md={6}>
          <Card elevation={1} sx={{ height: '100%', borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                Account Details
              </Typography>
              
              <List disablePadding>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Star sx={{ color: getTierColor() }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={<Typography variant="body2" color="text.secondary">Membership Tier</Typography>}
                    secondary={
                      <Chip 
                        label={getTierLabel()} 
                        size="small"
                        sx={{ 
                          mt: 0.5,
                          bgcolor: getTierColor(),
                          color: userRole === ROLES.CUSTOMER ? 'white' : 'black',
                          fontWeight: 'bold'
                        }} 
                      />
                    }
                  />
                </ListItem>
                
                <Divider sx={{ my: 1 }} />
                
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <CheckCircle color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary={<Typography variant="body2" color="text.secondary">Account Status</Typography>}
                    secondary={
                      <Chip 
                        label="Active" 
                        size="small" 
                        color="success"
                        sx={{ mt: 0.5, fontWeight: 'bold' }}
                      />
                    }
                  />
                </ListItem>
                
                <Divider sx={{ my: 1 }} />
                
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Favorite color="error" />
                  </ListItemIcon>
                  <ListItemText
                    primary={<Typography variant="body2" color="text.secondary">Favorite Properties</Typography>}
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Typography variant="h6" fontWeight="bold" color="primary">
                          {favoritesCount}
                        </Typography>
                        {favoritesCount > 0 && (
                          <Button 
                            size="small" 
                            onClick={() => navigate('/favorites')}
                            sx={{ textTransform: 'none' }}
                          >
                            View All
                          </Button>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Upgrade Card for Basic Users */}
        {!isProUser && userRole !== ROLES.ADMIN && (
          <Grid item xs={12}>
            <Card 
              elevation={1} 
              sx={{ 
                borderRadius: 2,
                background: 'linear-gradient(135deg, #1B5E20 0%, #2E7D32 100%)',
                color: 'white'
              }}
            >
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Star sx={{ fontSize: 48, color: '#ffd700', mb: 2 }} />
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  Upgrade to Pro Membership
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9, mb: 3, maxWidth: 600, mx: 'auto' }}>
                  Get full access to property details, upset values, profit calculations, and advanced analytics to make better investment decisions.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Upgrade />}
                  onClick={() => setSubscriptionModalOpen(true)}
                  sx={{
                    bgcolor: '#ffd700',
                    color: 'black',
                    fontWeight: 'bold',
                    '&:hover': {
                      bgcolor: '#ffed4e'
                    }
                  }}
                >
                  Upgrade Now - Free!
                </Button>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Pro Member Benefits Card */}
        {isProUser && (
          <Grid item xs={12}>
            <Card 
              elevation={1} 
              sx={{ 
                borderRadius: 2,
                background: 'linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)'
              }}
            >
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <Star sx={{ fontSize: 40, color: '#1B5E20', mb: 1 }} />
                <Typography variant="h6" fontWeight="bold" color="#1B5E20">
                  {userRole === ROLES.ADMIN ? 'Administrator Access' : 'Pro Member Benefits Active'}
                </Typography>
                <Typography variant="body2" color="#1B5E20" sx={{ mt: 1, opacity: 0.8 }}>
                  You have full access to all property details, profit calculations, and advanced analytics.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Subscription Modal */}
      <SubscriptionModal
        open={subscriptionModalOpen}
        onClose={() => setSubscriptionModalOpen(false)}
        feature="Pro Membership"
      />
    </Container>
  );
};

export default Profile;
